import React, { useState } from "react";
import { Select, Spin, Row, Col, Card, Statistic, Button } from "antd";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useGetEventsQuery } from "../../services/events";
import { useGetEventAnalyticsQuery } from "../../services/analytics";
import * as XLSX from "xlsx";

const { Option } = Select;

const EventAnalytics = () => {
    const [selectedEvent, setSelectedEvent] = useState(null);

    const { data: eventsData, isLoading: isLoadingEvents } = useGetEventsQuery({
        currentPage: 1,
        resultPerPage: 1000
    });

    const { data: analyticsData, isFetching: isFetchingAnalytics } = useGetEventAnalyticsQuery(
        selectedEvent,
        { skip: !selectedEvent }
    );

    const events = eventsData?.events?.data || [];
    const data = analyticsData?.data || null;

    const handleExport = () => {
        if (!data) return;
        const exportData = [{
            "Total Delegates": data.totalDelegates,
            "Total Sponsors": data.totalSponsors,
            "Cancellation %": data.cancellationPercent,
            "Show-up Total": data.showUpTotal,
            "Total Meetings": data.meetings?.total,
            "Accepted Meetings": data.meetings?.accepted,
            "Rejected Meetings": data.meetings?.rejected,
            "Pending Meetings": data.meetings?.pending
        }];
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Event Analytics");
        XLSX.writeFile(wb, "Event_Analytics.xlsx");
    };

    // Prepare day-wise show-up data for chart
    const showUpChartData = data?.showUpByDay
        ? Object.entries(data.showUpByDay).map(([day, counts]) => ({
            day,
            sponsors: counts.sponsors || 0,
            delegates: counts.delegates || 0,
            total: counts.total || 0,
        }))
        : [];

    return (
        <div>
            <div className="mb-6 flex justify-between items-center bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                <div>
                    <span className="mr-3 font-semibold text-gray-700">Select Event:</span>
                    <Select
                        style={{ width: 350 }}
                        placeholder="Select an Event..."
                        onChange={(val) => setSelectedEvent(val)}
                        loading={isLoadingEvents}
                        showSearch
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {events.map((e) => (
                            <Option key={e._id} value={e._id}>{e.name}</Option>
                        ))}
                    </Select>
                </div>
                <Button
                    type="primary"
                    disabled={!data}
                    onClick={handleExport}
                    className="bg-primary hover:bg-yellow-600 border-none"
                >
                    Export Data
                </Button>
            </div>

            {isFetchingAnalytics && (
                <div className="flex justify-center p-12"><Spin size="large" /></div>
            )}

            {!isFetchingAnalytics && data && (
                <div className="animate-fade-in">
                    <Row gutter={16} className="mb-6">
                        <Col span={6}>
                            <Card className="shadow-sm border-gray-100 hover:shadow-md transition-shadow">
                                <Statistic title="Total Delegates" value={data.totalDelegates} valueStyle={{ color: '#1890ff' }} />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card className="shadow-sm border-gray-100 hover:shadow-md transition-shadow">
                                <Statistic title="Total Sponsors" value={data.totalSponsors} valueStyle={{ color: '#52c41a' }} />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card className="shadow-sm border-gray-100 hover:shadow-md transition-shadow">
                                <Statistic title="Cancellation Rate" value={data.cancellationPercent} suffix="%" valueStyle={{ color: '#cf1322' }} />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card className="shadow-sm border-gray-100 hover:shadow-md transition-shadow">
                                <Statistic title="Total Show-ups" value={data.showUpTotal} valueStyle={{ color: '#3f8600' }} />
                            </Card>
                        </Col>
                        <Col span={6} className="mt-4">
                            <Card className="shadow-sm border-gray-100 hover:shadow-md transition-shadow">
                                <Statistic title="Total Meeting Requests" value={data.meetings?.total} valueStyle={{ color: '#E2B024' }} />
                            </Card>
                        </Col>
                    </Row>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" style={{ height: 450 }}>
                            <h3 className="font-semibold text-lg text-gray-700 mb-6">Meeting Requests Breakdown</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={[
                                        { name: "Accepted", count: data.meetings?.accepted || 0, fill: "#52c41a" },
                                        { name: "Pending", count: data.meetings?.pending || 0, fill: "#faad14" },
                                        { name: "Rejected", count: data.meetings?.rejected || 0, fill: "#f5222d" }
                                    ]}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <XAxis dataKey="name" tick={{ fill: '#666' }} axisLine={{ stroke: '#e8e8e8' }} />
                                    <YAxis tick={{ fill: '#666' }} axisLine={{ stroke: '#e8e8e8' }} />
                                    <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" style={{ height: 450 }}>
                            <h3 className="font-semibold text-lg text-gray-700 mb-6">Day-wise Show-ups by User Type</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={showUpChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <XAxis dataKey="day" tick={{ fill: '#666' }} axisLine={{ stroke: '#e8e8e8' }} />
                                    <YAxis tick={{ fill: '#666' }} axisLine={{ stroke: '#e8e8e8' }} />
                                    <Tooltip
                                        cursor={{ fill: '#f5f5f5' }}
                                        contentStyle={{
                                            borderRadius: '8px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="sponsors" name="Sponsors" stackId="a" fill="#52c41a" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="delegates" name="Delegates" stackId="a" fill="#1890ff" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {!isFetchingAnalytics && !data && selectedEvent && (
                <div className="text-center text-gray-400 mt-10">
                    No data found for this event.
                </div>
            )}
            {!selectedEvent && (
                <div className="text-center text-gray-400 mt-20 flex flex-col items-center">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4 text-gray-300">
                        <path d="M9 19C9 20.1 9.9 21 11 21H13C14.1 21 15 20.1 15 19V17H9V19ZM12 2C7.03 2 3 6.03 3 11C3 13.38 3.93 15.54 5.46 17.15C5.8 17.51 6 17.98 6 18.47V19C6 19.55 6.45 20 7 20H17C17.55 20 18 19.55 18 19V18.47C18 17.98 18.2 17.51 18.54 17.15C20.07 15.54 21 13.38 21 11C21 6.03 16.97 2 12 2ZM17.13 15.74C16.42 16.48 16 17.44 16 18.47H8C8 17.44 7.58 16.48 6.87 15.74C5.7 14.53 5 12.84 5 11C5 7.14 8.14 4 12 4C15.86 4 19 7.14 19 11C19 12.84 18.3 14.53 17.13 15.74Z" fill="currentColor" />
                    </svg>
                    <p className="text-lg">Please select an event to view its analytics.</p>
                </div>
            )}
        </div>
    );
};

export default EventAnalytics;
